/**
 * Price and shipping cost rules of the product view
 */
import type { PriceRange, ShippingConfig } from '../types'

export interface PriceSummary {
  regularPrice: number
  finalPrice: number
  /** Whole percent off, null without a discount */
  discount: number | null
  hasDiscount: boolean
}

export function getPriceSummary(minimumPrice: PriceRange['minimum_price']): PriceSummary {
  const regularPrice = minimumPrice?.regular_price?.value ?? 0
  const finalPrice = minimumPrice?.final_price?.value ?? 0
  const discountPercent = minimumPrice?.discount?.percent_off

  return {
    regularPrice,
    finalPrice,
    discount: discountPercent ? Math.round(discountPercent) : null,
    hasDiscount: finalPrice < regularPrice,
  }
}

/** The msrp custom attribute as a price, null when it is missing, not a number or not positive */
export function parseMsrp(msrp: string | null): number | null {
  const msrpValue = parseFloat(msrp ?? '0')

  return !isNaN(msrpValue) && msrpValue > 0 ? msrpValue : null
}

export interface ShippingCost {
  isFreeShipping: boolean
  /** null when shipping is free or no shipping price is known */
  price: number | null
}

/** A category shipping price wins over the store shipping price and its free shipping threshold */
export function getShippingCost(
  finalPrice: number,
  categoryShippingPrice: number | null | undefined,
  storeShipping: Pick<ShippingConfig, 'shippingPrice' | 'shippingPriceFormatted' | 'freeShippingThreshold'>
): ShippingCost {
  const { shippingPrice, shippingPriceFormatted, freeShippingThreshold } = storeShipping
  const hasCategoryShippingPrice =
    typeof categoryShippingPrice === 'number' && Number.isFinite(categoryShippingPrice)

  const isFreeShipping = hasCategoryShippingPrice
    ? categoryShippingPrice <= 0
    : freeShippingThreshold > 0 && finalPrice >= freeShippingThreshold

  if (isFreeShipping) {
    return { isFreeShipping, price: null }
  }

  if (hasCategoryShippingPrice) {
    return { isFreeShipping, price: categoryShippingPrice }
  }

  return { isFreeShipping, price: shippingPriceFormatted ? shippingPrice : null }
}
